/**
 * Branding utilities for multi-tenant customization
 */

export interface BrandingConfig {
  logo: string | null;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * Default branding values
 */
export const DEFAULT_BRANDING: BrandingConfig = {
  logo: null,
  primaryColor: "#3b82f6", // blue-500
  secondaryColor: "#8b5cf6", // violet-500
};

/**
 * Convert hex color to RGB values
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color (WCAG formula)
 */
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  if (!rgb1 || !rgb2) return 1;

  const l1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Generate contrasting text color (black or white) based on background color
 * Uses WCAG contrast ratio guidelines
 */
export function getContrastingTextColor(backgroundColor: string): string {
  const whiteContrast = getContrastRatio(backgroundColor, "#ffffff");
  const blackContrast = getContrastRatio(backgroundColor, "#000000");

  // Return the color with better contrast
  return whiteContrast > blackContrast ? "#ffffff" : "#000000";
}

/**
 * Lighten a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * (percent / 100)));
  const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * (percent / 100)));
  const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * (percent / 100)));

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Darken a hex color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.floor(rgb.r * (1 - percent / 100)));
  const g = Math.max(0, Math.floor(rgb.g * (1 - percent / 100)));
  const b = Math.max(0, Math.floor(rgb.b * (1 - percent / 100)));

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Convert hex color to HSL string format for CSS variables
 */
export function hexToHsl(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "0 0% 50%";

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${h} ${s}% ${lPercent}%`;
}

/**
 * Apply branding CSS variables to the document
 */
export function applyBrandingToDocument(branding: BrandingConfig): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // Convert hex colors to HSL format for the CSS variables
  const primaryHsl = hexToHsl(branding.primaryColor);
  const secondaryHsl = hexToHsl(branding.secondaryColor);

  // Set primary color in HSL format (this will cascade to --color-primary via globals.css)
  root.style.setProperty("--primary", primaryHsl);

  // Set secondary color in HSL format (this will cascade to --color-secondary via globals.css)
  root.style.setProperty("--secondary", secondaryHsl);

  // Also set the direct color variables for components that use them directly
  root.style.setProperty("--color-primary", branding.primaryColor);
  root.style.setProperty("--color-primary-text", getContrastingTextColor(branding.primaryColor));
  root.style.setProperty("--color-primary-light", lightenColor(branding.primaryColor, 20));
  root.style.setProperty("--color-primary-dark", darkenColor(branding.primaryColor, 20));

  root.style.setProperty("--color-secondary", branding.secondaryColor);
  root.style.setProperty("--color-secondary-text", getContrastingTextColor(branding.secondaryColor));
  root.style.setProperty("--color-secondary-light", lightenColor(branding.secondaryColor, 20));
  root.style.setProperty("--color-secondary-dark", darkenColor(branding.secondaryColor, 20));

  // Set logo URL if available
  if (branding.logo) {
    root.style.setProperty("--logo-url", `url(${branding.logo})`);
  }
}

/**
 * Validate hex color format
 */
export function isValidHexColor(hex: string): boolean {
  return /^#[0-9A-F]{6}$/i.test(hex);
}

/**
 * Normalize hex color (ensure # prefix and uppercase)
 */
export function normalizeHexColor(hex: string): string {
  let normalized = hex.trim();
  if (!normalized.startsWith("#")) {
    normalized = `#${normalized}`;
  }
  return normalized.toUpperCase();
}
